


































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import PieChart from "@/components/PieChart.vue";
import SubEntryGroupingTable from "@/components/Investor/SubEntryGroupingTable.vue";
import MediaValue from "@/interfaces/MediaValue";
import Entry from "@/models/Entry";
import InvestorsMixin from "@/mixins/http/Investor";
import { AdcontrolProfile, Media, User } from "@/models";
import ColorHelper from "@/services/ColorHelper";
import { CustomChartItem } from "../CustomChart.vue";
import { ProfileCapabilitiesService } from "@/services/Profile/CapabilitiesService";
import ClientUserInfo from "@/models/Agents/UserInfo";
import { Getter } from "vuex-class";

export interface SubEntryGroupingFilter {
  date: { from: string; to: string };
  media: MediaValue;
  unitOfMeasure?: string;
}

const chartEntriesTag = "chart_entries";

@Component({
  components: {
    PieChart,
    SubEntryGroupingTable,
  },
})
export default class SubEntryGroupingCard extends Mixins(InvestorsMixin) {
  @Prop({ required: true }) readonly entryId!: string;
  @Prop({ required: true }) readonly entryType!: string;
  @Prop({ required: true }) readonly filter!: SubEntryGroupingFilter;
  @Prop({ default: true }) readonly spotTypeDetail!: boolean;
  @Prop() readonly grpTargetId!: string;
  @Prop({ required: true }) readonly adcontrolProfile!: AdcontrolProfile;

  @Getter("user") readonly user!: User;
  @Getter("profile") readonly profile!: AdcontrolProfile;
  @Getter("agentUserInfos") readonly agentUserInfo?: ClientUserInfo;

  items: Array<Entry> = [];
  loading = false;

  chart = {
    type: "doughnut",
  };

  created() {
    this.loadChartEntries();
  }

  @Watch("entryId")
  onEntryIdChanged() {
    this.loadChartEntries();
  }

  @Watch("entryType")
  onEntryTypeChanged() {
    this.loadChartEntries();
  }

  @Watch("filter", { deep: true })
  onFilterChanged() {
    this.loadChartEntries();
  }

  @Watch("grpTargetId")
  onGrpTargetIdChanged() {
    this.loadChartEntries();
  }

  @Watch("adcontrolProfile")
  onProfileChanged() {
    this.loadChartEntries();
  }

  get onlyVod(): boolean {
    return this.adcontrolProfile?.mediaSourceKind === "msk_vod";
  }

  get postParams() {
    const date = this.filter.date;
    const media = Media.mediaToList(this.filter.media);
    const date_range = [date.from, date.to];
    const grpTarget = this.grpTargetId;
    // eslint-disable-next-line
    const data: any = {
      filter: { date_range, media, grpTarget },
      type: this.filter.unitOfMeasure,
      vod: this.onlyVod,
    };
    return data;
  }

  get entryHierarchy() {
    let subEntryType = null;
    let entryType = null;

    switch (this.entryType) {
      case "companies":
        entryType = "company";
        subEntryType = "brands";
        break;
      case "brands":
        entryType = "brand";
        subEntryType = "products";
        break;
    }

    if (!entryType || !subEntryType) {
      return null;
    }

    return { entryType, subEntryType };
  }

  get applyAgentsFilter(): boolean {
    return (
      ProfileCapabilitiesService.agentsModuleEnabled(
        this.user.profile,
        this.agentUserInfo
      ) && !(this.agentUserInfo?.superUser ?? false)
    );
  }

  async loadChartEntries() {
    if (!this.filter) {
      return;
    }
    if (!this.entryHierarchy) {
      return;
    }
    this.loading = true;

    const { entryType, subEntryType } = this.entryHierarchy;
    const data = this.postParams;
    data.filter[entryType] = [parseInt(this.entryId)];

    if (this.applyAgentsFilter) {
      data.agent_module_view = true;
    }

    this.items = await this.getChartEntries(
      subEntryType,
      data,
      chartEntriesTag
    );
    this.loading = false;
    this.$emit("loaded", {
      count: this.items.length,
      hasSponsor: !!this.items.find(
        (e) => (e.tvSponsorCount ?? 0) + (e.radioSponsorCount ?? 0) > 0
      ),
    });
  }

  get showTvChart() {
    return this.filter.media.tv && this.itemsForTvPieChart.length;
  }

  get showRadioChart() {
    return this.filter.media.radio && this.itemsForRadioPieChart.length;
  }

  itemsForPieChart(media: string) {
    ColorHelper.reset();
    const colors: string[] = [];
    const chartItems: CustomChartItem[] = [];
    this.items.forEach((o) => {
      const color = ColorHelper.pick();
      colors.push(color);
      const val =
        (media === Media.tv
          ? o.tvCount
          : media === Media.radio
          ? o.radioCount
          : 0) ?? o.detectionsCount;
      if (val) {
        chartItems.push(this.createChartItem(o.name, val, 0, color));
      }
    });

    if (this.spotTypeDetail) {
      this.items.forEach((o, index) => {
        const tab =
          media === Media.tv
            ? o.tvTabCount
            : media === Media.radio
            ? o.radioTabCount
            : 0;
        const sponsor =
          media === Media.tv
            ? o.tvSponsorCount
            : media === Media.radio
            ? o.radioSponsorCount
            : 0;
        if (tab) {
          chartItems.push(
            this.createChartItem(
              o.name + " Tabellare",
              tab,
              1,
              ColorHelper.adjust(colors[index], 30)
            )
          );
        }
        if (sponsor) {
          chartItems.push(
            this.createChartItem(
              o.name + " Sponsorizzazioni",
              sponsor,
              1,
              ColorHelper.adjust(colors[index], 60)
            )
          );
        }
      });
    }

    return chartItems;
  }

  get itemsForTvPieChart() {
    return this.itemsForPieChart(Media.tv);
  }

  get itemsForRadioPieChart() {
    return this.itemsForPieChart(Media.radio);
  }

  createChartItem = (
    name: string,
    value: number,
    level = 0,
    color?: string
  ) => {
    if (this.filter.unitOfMeasure == "grp_over_spots") {
      return {
        data: [Number.parseFloat(Number.parseFloat(`${value}`).toFixed(2))],
        label: name,
        level,
        color,
      };
    } else {
      return { data: [value], label: name, level, color };
    }
  };
}
